import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'
import { SERVICE_TYPES } from '@/app/registry/service-registry/_utils/constants'

export type ServiceRegistryCreateUpdateAPIResponse = {
  tools: string[]
}

export type ServiceRegistryCreateUpdateParams = {
  name: string
  serviceType: string
  url: string
  key: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  scope?: string[]
  token_type?: string
  clientId?: string
  tokenUrl?: string
}

export function useCreateServiceRegistryMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()
  return useMutation<
    ServiceRegistryCreateUpdateAPIResponse,
    Error,
    ServiceRegistryCreateUpdateParams
  >({
    mutationKey: ['create-service-registry', domainId],
    mutationFn: async (
      params: ServiceRegistryCreateUpdateParams,
    ): Promise<ServiceRegistryCreateUpdateAPIResponse> => {
      const requestBody = transformParamsToRequestBody(params)
      const response = await apiRequest({
        path: taskManagerApiPaths.mcpCreate({ domainId }),
        options: {
          method: 'POST',
          body: requestBody,
        },
      })

      if (!response.ok) {
        throw new Error('FAILED_TO_CREATE_SERVICE_REGISTRY')
      }

      const data = (await response.json()) as ServiceRegistryCreateUpdateAPIResponse

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-registry', domainId] })
    },
  })
}

export function transformParamsToRequestBody(params: ServiceRegistryCreateUpdateParams): string {
  switch (params.serviceType) {
    case SERVICE_TYPES.MCP_KEY:
      return JSON.stringify({
        name: params?.name,
        service_type: params?.serviceType,
        url: params?.url,
        key: params?.key,
      })
    case SERVICE_TYPES.MCP_OAUTH2:
      return JSON.stringify({
        name: params?.name,
        service_type: params?.serviceType,
        url: params?.url,
        access_token: params?.access_token,
        refresh_token: params?.refresh_token,
        expires_in: params?.expires_in,
        scopes: params?.scope,
        token_type: params?.token_type,
        oauth_client_id: params?.clientId,
        token_url: params?.tokenUrl,
      })
    default:
      throw new Error('INVALID_SERVICE_TYPE')
  }
}
